import { checkoutFetch, withCheckoutDeadline } from './checkoutDeadline.js';

export function collectionProviderFromReadiness(readiness, paymentMethod) {
  if (paymentMethod === 'bit') return 'icount';
  if (!['icount', 'takbull'].includes(readiness?.collection_provider)) throw new Error('payment_provider_missing');
  return readiness.collection_provider;
}
export function validatedTakbullCheckoutUrl(value) {
  if (typeof value !== 'string' || value.length > 2048) return null;
  try {
    const url = new URL(value);
    return url.origin === 'https://api.takbull.co.il'
      && /^https:\/\/api\.takbull\.co\.il\/PaymentGateway\?orderUniqId=[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(value)
      && !url.username && !url.password && !url.port && !url.hash && url.href === value ? value : null;
  } catch (_) { return null; }
}
export function paymentStatusUrl(search) {
  const params = new URLSearchParams(search);
  const checkoutId = params.get('checkoutId');
  if (checkoutId !== null) return /^[a-f0-9]{40}$/.test(checkoutId)
    ? `/api/payments/status?checkoutId=${checkoutId}` : null;
  const idem = String(params.get('idem') || '').trim();
  return idem.length >= 6 && idem.length <= 200 ? `/api/payments/status?idem=${encodeURIComponent(idem)}` : null;
}

export function validatedPaymentContinuation(value) {
  return value?.collection_provider === 'takbull' && value.currency === 'ILS'
    && typeof value.payment_attempt_key === 'string' && /^pay-[a-f0-9-]{36}$/.test(value.payment_attempt_key)
    && typeof value.order_id === 'string' && value.order_id.length > 0 && value.order_id.length <= 200
    && Number.isSafeInteger(value.amount_agorot) && value.amount_agorot > 0
    && typeof value.customer_name === 'string' && value.customer_name.length > 0
    && typeof value.description === 'string' && value.description.length > 0 ? value : null;
}

export async function resumeTakbullPayment({ orderKey, continuation, checkoutIntent, fetcher = checkoutFetch }) {
  if (!validatedPaymentContinuation(continuation)) throw new Error('invalid_payment_continuation');
  const response = await fetcher('/api/takbull/launch', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idempotency_key: orderKey, payment_attempt_key: continuation.payment_attempt_key,
      checkout_intent: checkoutIntent }),
  });
  const result = await response.json().catch(() => null);
  if (response.ok && result?.collected === true && result.launched === false
    && result.collection_provider === 'takbull' && result.payment_attempt_key === continuation.payment_attempt_key
    && /^\/thank-you-cc\?checkoutId=[a-f0-9]{40}$/.test(result.payment_status_url)) return result.payment_status_url;
  const url = validatedTakbullCheckoutUrl(result?.payment_url);
  if (!response.ok || result?.launched !== true || result.collection_provider !== 'takbull'
    || result.payment_attempt_key !== continuation.payment_attempt_key || !url) {
    const error = new Error('payment_continuation_blocked');
    error.code = result?.state;
    error.collected = continuation.collected === true || result?.collected === true
      || result?.state === 'payment_already_collected';
    throw error;
  }
  return url;
}

export async function preparePaymentCheckout({ orderKey, amountAgorot, paymentMethod,
  makeCheckoutIntent, fetcher = checkoutFetch }) {
  const response = await fetcher('/api/icount/prepare-order-edit', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idempotency_key: orderKey }),
  });
  const result = await response.json().catch(() => null);
  if (response.ok && result?.allowed === true) return { kind: 'ready' };
  if (['payment_continuation', 'prior_attempt_issued'].includes(result?.state)) {
    const continuation = validatedPaymentContinuation(result.continuation);
    if (continuation) {
      // This descriptor is exclusively for an existing Takbull card attempt.
      // A known amount/method change cannot use it. Do not download artwork or
      // contact the old hosted request before taking the approved fresh path.
      const changed = (paymentMethod !== undefined && paymentMethod !== 'card')
        || (Number.isSafeInteger(amountAgorot) && amountAgorot !== continuation.amount_agorot);
      if (changed) {
        if (continuation.collected === true) {
          const error = new Error('payment_already_collected'); error.code = 'payment_already_collected'; throw error;
        }
        return { kind: 'fresh', reason: 'checkout_details_changed' };
      }
      try {
        const checkoutIntent = await withCheckoutDeadline((signal) => makeCheckoutIntent(signal), { timeoutMs: 10000 });
        const url = await resumeTakbullPayment({ orderKey, continuation, checkoutIntent, fetcher });
        return { kind: 'continue', url };
      } catch (error) {
        if (continuation.collected === true || error.collected === true) {
          const collected = new Error('payment_already_collected'); collected.code = 'payment_already_collected'; throw collected;
        }
        return { kind: 'fresh', reason: error.code || 'continuation_unavailable' };
      }
    }
    if (result.state === 'payment_continuation' && result.continuation?.collected === true) {
      const error = new Error('payment_already_collected'); error.code = 'payment_already_collected'; throw error;
    }
    return { kind: 'fresh', reason: result.state };
  }
  if (result?.state === 'prior_attempt_unverified') return { kind: 'fresh', reason: result.state };
  const error = new Error('payment_checkout_unavailable'); error.code = result?.state; throw error;
}
